<template>
  <BlockFieldset class="project-customer-additional-information">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('field')"
          label-for="field"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="project.customer.field" type="text" :placeholder="$t('field')" />
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('fieldSector')"
          label-for="fieldSector"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="invalidFeedbackNA"
          :state="requirementState('fieldSector')"
        >
          <b-form-input
            v-model="project.customer.fieldSector"
            type="text"
            :placeholder="$t('fieldSector')"
            :state="requirementState('fieldSector')"
          />
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('boundary')"
          label-for="boundary"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="invalidFeedbackNA"
          :state="requirementState('boundary')"
        >
          <b-form-input
            v-model="project.customer.boundary"
            type="text"
            :placeholder="$t('boundary')"
            :state="requirementState('boundary')"
          />
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('electricMeterNumberLabel') }}</strong>
          <div v-if="canChangeElectricMeterNumbers" class="button-right">
            <b-button
              size="sm"
              variant="success"
              class="add-electric-meter-number"
              :title="$t('add')"
              @click="addElectricMeterNumber"
            >
              <i class="fa fa-plus" />
            </b-button>
          </div>
        </legend>

        <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
        <template v-else>
          <div
            v-for="(entry, index) in electricMeterNumbers"
            :key="index"
            class="electric-meter-number"
          >
            <b-form-group
              horizontal
              :label="$t('electricMeterNumberLabel')"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
              :invalid-feedback="invalidFeedbackNA"
              :state="electricMeterNumberState(index)"
            >
              <b-form-input
                v-model="entry.number"
                type="text"
                :placeholder="$t('electricMeterNumberLabel')"
                :state="electricMeterNumberState(index)"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('notes')"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input v-model="entry.description" type="text" :placeholder="$t('notes')" />
            </b-form-group>
            <div v-if="canChangeElectricMeterNumbers" class="text-right">
              <b-button
                size="sm"
                variant="outline-danger"
                class="remove-electric-meter-number"
                :title="$t('remove')"
                @click="removeElectricMeterNumber(index)"
              >
                <i class="fa fa-trash-o" />
              </b-button>
            </div>
            <hr />
          </div>
        </template>

        <legend>
          <strong>{{ $t('notes') }}</strong>
        </legend>
        <b-form-group horizontal breakpoint="md">
          <ckeditor
            v-model="project.customerDescription"
            :editor="editor"
            :config="editorConfig"
            :disabled="isBlockReadOnly"
          ></ckeditor>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t('project_customer_additional_information') }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { isRequirementFulfilled } from '@/helpers/registrationRequirements';
import { toProjectCustomerAdditionalInformationInput } from './projectCustomerAdditionalInformationInput';

/** Number of the unsaved project (ProjectEdit `_new`): its values go with `createProject`. */
const NEW_PROJECT_NUMBER = '_new';

/** Registration requirement of the first meter number (the fixed field it is mirrored into). */
const FIRST_ELECTRIC_METER_NUMBER_REQUIREMENT = 'electricMeterNumber';

/**
 * Block project-customer-additional-information
 * (plan docs/work/_done/2026-10-project-block-customer-additional-information): land register fields,
 * the list of meter numbers with notes and the customer notes, own save button. On the unsaved
 * project the list shows a hint (decided 3), the other fields go with `createProject`.
 * Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectCustomerAdditionalInformation',
  components: { BlockFieldset, ckeditor: CKEditor.component },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return {
      editor: InlineEditor,
      editorConfig: {},
    };
  },
  computed: {
    blockInput() {
      return toProjectCustomerAdditionalInformationInput(this.project);
    },
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && !this.isNewProject;
    },
    canChangeElectricMeterNumbers() {
      return !this.isBlockReadOnly && !this.isNewProject;
    },
    electricMeterNumbers() {
      return this.project.customer.electricMeterNumbers ?? [];
    },
    invalidFeedbackNA() {
      return 'Voraussetzung NA';
    },
  },
  methods: {
    ...mapActions(['updateProjectCustomerAdditionalInformation']),
    saveBlockInput(input) {
      return this.updateProjectCustomerAdditionalInformation({
        projectNumber: this.project.number,
        input,
      });
    },
    requirementState(name) {
      return isRequirementFulfilled(this.project.netRegistrationRequirements, name);
    },
    electricMeterNumberState(index) {
      if (index !== 0) {
        return null;
      }

      return this.requirementState(FIRST_ELECTRIC_METER_NUMBER_REQUIREMENT);
    },
    addElectricMeterNumber() {
      const emptyEntry = { number: '', description: '' };
      if (!this.project.customer.electricMeterNumbers) {
        this.$set(this.project.customer, 'electricMeterNumbers', [emptyEntry]);
        return;
      }

      this.project.customer.electricMeterNumbers.push(emptyEntry);
    },
    removeElectricMeterNumber(index) {
      this.project.customer.electricMeterNumbers.splice(index, 1);
    },
  },
};
</script>
